// db.ts
import { formatISO } from 'date-fns'
import {
  AppStatus,
  LogItemDex,
  Message,
  Plb,
  User,
  Walk,
  WalkStatus,
} from './dbTypes'
import { Dexie } from 'dexie'
import dexieCloud, { DexieCloudTable } from 'dexie-cloud-addon'
import { applyEncryption } from './applyEncryption'

// ---------------------------------------------------------
// Database
// ---------------------------------------------------------
const DB_DESIGN_VERSION = 4

export const db = new Dexie('TripsheetsDb', {
  addons: [dexieCloud],
}) as Dexie & {
  appStatus: DexieCloudTable<AppStatus, 'appName'>
  logItems: DexieCloudTable<LogItemDex, 'id'>
  users: DexieCloudTable<User, 'userId'>
  walks: DexieCloudTable<Walk, 'walkId'>
  plbs: DexieCloudTable<Plb, 'plbId'>
  messages: DexieCloudTable<Message, 'messageId'>
}

applyEncryption(db)

db.version(DB_DESIGN_VERSION).stores({
  appStatus: 'appName', // stays local
  logItems: '@id', // stays local
  users: 'userId', // cloud
  walks: 'walkId', // cloud
  plbs: 'plbId', // cloud
  messages: 'messageId', // cloud
})

// ---------------------------------------------------------
// Startup logic
// ---------------------------------------------------------
const defaultAppStatus: AppStatus = {
  appName: 'Tripsheets',
  navTo: 'start',
  userId: '',
  clubId: '',
  headerMessage: '',
  haveInternet: false,
  isDarkTheme: false,
  isClosed: false,
  parameters: '',
  messagesSeenTo: formatISO(new Date(Date.now() - 24 * 60 * 60 * 1000)),
  messagesQueue: [],
  zoomDefault: 0,
  tipsAdmin: {},
}

const initialiseDexie = async () => {
  // Dexie Cloud configuration
  try {
    db.cloud.configure({
      databaseUrl: import.meta.env.VITE_DEXIE_CLOUD_URL,
      requireAuth: true,
      unsyncedTables: ['appStatus', 'logItems', '_encryptionSettings'],
      tryUseServiceWorker: false, // Recommended for encryption
      fetchTokens: async (tokenParams) => {
        const fbFunctionUrl = 'https://dexiecloudtoken-b23wc7jabq-uc.a.run.app/'
        try {
          const res = await fetch(fbFunctionUrl, {
            method: 'post',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...tokenParams,
              environment: import.meta.env.VITE_ENVIRONMENT,
            }),
          })
          const result = await res.json()
          return result
        } catch (error) {
          console.error('Error during Dexie token fetch:', error)
        }
      },
    })
    console.log('Dexie Cloud configuration successful')
  } catch (error) {
    console.error('Error during Dexie Cloud configuration:', error)
  }

  // Dexie Cloud login
  try {
    await db.cloud.login()
    console.log('Dexie login successful')
  } catch (error) {
    console.error('Error during Dexie login:', error)
    return
  }

  // Local-only AppStatus table setup
  let currentAppStatus = await db.appStatus.get('Tripsheets')
  if (!currentAppStatus) {
    await db.appStatus.add(defaultAppStatus)
    currentAppStatus = await db.appStatus.get('Tripsheets')
  }
  if (!currentAppStatus) return

  // Define the club id
  const isMissing = !currentAppStatus.clubId
  const isValid =
    currentAppStatus.clubId === import.meta.env.VITE_ENVIRONMENT ||
    currentAppStatus.clubId === 'INCORRECT-ENVIRONMENT'
  const clubId =
    isMissing || isValid
      ? import.meta.env.VITE_ENVIRONMENT
      : 'INCORRECT-ENVIRONMENT'

  // Define/reset some app statusses on app start
  const statusOnStart: Partial<AppStatus> = {
    clubId,
    isClosed: false,
    messagesSeenTo:
      currentAppStatus.messagesSeenTo ||
      formatISO(new Date(Date.now() - 12 * 60 * 60 * 1000)),
  }
  await db.appStatus.update('Tripsheets', statusOnStart)

  // Go sync the cloud tables
  try {
    const res = await db.cloud.sync()
    console.log('Initial sync completed')
  } catch (error) {
    console.error('Error during initial dexie sync:', error)
  }
}

initialiseDexie().then((r) => {
  console.log('Database initialisation completed', r)
})

export const walkStatusses: WalkStatus[] = [
  'draft',
  'open',
  'started',
  'ended',
  'cancelled',
  'deleted',
]
